// Two modes:
//   - Connect: prompt the user for a nickname + colour, then redirect to
//     Google's OAuth for a fresh shared-inbox connect.
//   - Rename: edit the nickname (and colour) of an existing shared account.
//     Hits PUT /api/accounts/:id/nickname which find-or-creates the tag and
//     backfills thread_tags for every existing thread of that account.

import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { startConnectGmail, useSetAccountNickname, type GmailAccount } from '@/lib/accounts';
import { TAG_COLORS, tagDotClass } from '@/lib/tag-colors';
import { cn } from '@/lib/utils';

export function SharedInboxDialog({
  trigger,
  open: openProp,
  onOpenChange,
  account,
}: {
  trigger?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** When present, the dialog renames this existing account instead of starting OAuth. */
  account?: GmailAccount;
}) {
  const [openState, setOpenState] = useState(false);
  const open = openProp ?? openState;
  const setOpen = onOpenChange ?? setOpenState;
  const [nickname, setNickname] = useState('');
  const [color, setColor] = useState<string>('sky');
  const setNickMutation = useSetAccountNickname();

  const isRename = !!account;

  // Hydrate from the account each time the dialog opens.
  useEffect(() => {
    if (!open) return;
    if (account) {
      setNickname(account.nickname ?? '');
      setColor(account.nicknameColor ?? 'sky');
    } else {
      setNickname('');
      setColor('sky');
    }
  }, [open, account]);

  function submit() {
    const n = nickname.trim();
    if (!n) return;
    if (isRename && account) {
      setNickMutation.mutate(
        { id: account.id, nickname: n, color },
        { onSuccess: () => setOpen(false) },
      );
    } else {
      startConnectGmail('shared', { nickname: n, nicknameColor: color });
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isRename ? 'Rename shared inbox' : 'Connect shared inbox'}
          </DialogTitle>
          <DialogDescription>
            {isRename
              ? `Renaming ${account?.emailAddress}. Every existing and future thread in this inbox will be tagged with the new name.`
              : 'Give this shared inbox a nickname — every email that lands here will be auto-tagged with it so you can spot them at a glance.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="mb-1.5 block text-xs font-medium">Nickname</label>
            <input
              type="text"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              placeholder="e.g. Support, Sales, info@…"
              maxLength={64}
              autoFocus
              className="w-full rounded-md border bg-background px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-ring"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  submit();
                }
              }}
            />
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-medium">Tag colour</label>
            <div className="flex flex-wrap gap-1.5">
              {TAG_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  className={cn(
                    'size-6 rounded-full ring-2 ring-transparent transition',
                    tagDotClass(c),
                    color === c && 'ring-foreground/40',
                  )}
                  title={c}
                />
              ))}
            </div>
          </div>

          {setNickMutation.error && (
            <p className="text-xs text-destructive">
              {setNickMutation.error instanceof Error
                ? setNickMutation.error.message
                : 'Failed to save'}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} disabled={setNickMutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={!nickname.trim() || setNickMutation.isPending}
            className="gap-1.5"
          >
            {setNickMutation.isPending && <Loader2 className="size-3.5 animate-spin" />}
            {isRename ? 'Save' : 'Continue to Google'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
